import React, { useState, useEffect, useCallback } from 'react';
import { MdPerson } from 'react-icons/md';
import './PermissionRights.css';
import Select from 'react-select';
import axios from 'axios';
import { GET_ALL_EMPLOYEES_API, FETCH_EMPLOYEE_PERMISSIONS_API, ASSIGN_PERMISSION_API } from '../config/API';
import { useUser } from '../context/UserContext';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import PaginatedSelect from '../components/common/PaginatedSelect';
import Swal from 'sweetalert2';


const PermissionRights = () => {
    const { menus: availableMenus, userProfile, refreshUserAccess, hasPermission } = useUser();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingPermissions, setFetchingPermissions] = useState(false);
    const [saving, setSaving] = useState(false);

    const [moduleList, setModuleList] = useState([]);

    useEffect(() => {
        if (availableMenus && availableMenus.length > 0) {
            setModuleList(availableMenus.map(menu => ({
                id: menu.id,
                name: menu.menu_name,
                constant: menu.menu_constant,
                order: menu.menu_order_no || 0
            })).sort((a, b) => a.order - b.order));
        }
    }, [availableMenus]);


    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [permissions, setPermissions] = useState({});


    // Initialize permissions when employee is selected
    const handleEmployeeChange = async (option) => {
        setSelectedEmployee(option);

        if (!option) {
            setPermissions({});
            return;
        }

        try {
            setFetchingPermissions(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(FETCH_EMPLOYEE_PERMISSIONS_API(option.value), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                // Initialize default permissions for ALL modules we currently have in moduleList
                const fetchedPermissions = {};

                // Use existing moduleList (from admin's view) as the base
                const currentModules = moduleList.length > 0 ? moduleList : (availableMenus || []).map(menu => ({
                    id: menu.id,
                    name: menu.menu_name,
                    constant: menu.menu_constant
                }));

                currentModules.forEach(module => {
                    fetchedPermissions[module.id] = {
                        create: false,
                        read: false,
                        update: false,
                        delete: false,
                    };
                });

                const employeePermissions = response.data.data || [];

                employeePermissions.forEach(perm => {
                    const menuId = perm.menu_id;
                    if (fetchedPermissions[menuId]) {
                        fetchedPermissions[menuId] = {
                            create: !!perm.can_create,
                            read: !!perm.can_read,
                            update: !!perm.can_update,
                            delete: !!perm.can_delete,
                        };
                    }
                });
                setPermissions(fetchedPermissions);
            }
        } catch (error) {
            console.error('Error fetching employee permissions:', error);
            // On error, fallback to default permissions for current modules
            const fallbackPerms = {};
            moduleList.forEach(module => {
                fallbackPerms[module.id] = { create: false, read: false, update: false, delete: false };
            });
            setPermissions(fallbackPerms);
        } finally {
            setFetchingPermissions(false);
        }
    };


    const DEPENDENCY_MAP = {
        'PRODUCTS': ['PRODUCT_CATEGORIES', 'UNIT'],
        'PROJECTS': ['CUSTOMERS', 'VENDORS', 'PROJECT_CATEGORIES', 'PROJECT_SUB_CATEGORIES', 'PROJECT_SUB_CATEGORY_DETAILS'],
        'PAYMENT_MANAGEMENT': ['VENDORS', 'CUSTOMERS', 'PROJECTS'],
        'PURCHASE_ORDERS': ['PRODUCTS', 'UNIT', 'PROJECTS', 'VENDORS'],
        'PO_MANAGEMENT': ['VENDORS', 'PROJECTS'],
        'PROJECT_SUB_CATEGORIES': ['PROJECT_CATEGORIES'],
        'PROJECT_SUB_CATEGORY_DETAILS': ['PROJECT_CATEGORIES', 'PROJECT_SUB_CATEGORIES'],
        'COMPARE_PO': ['PURCHASE_ORDERS'],
        'VIEW_QUOTATIONS': ['PURCHASE_ORDERS'],
        'INWARD_MANAGEMENT': ['PURCHASE_ORDERS', 'VENDORS', 'PROJECTS'],
        'PROJECT_EXPENSE_REPORT': ['PROJECTS', 'VENDORS', 'PURCHASE_ORDERS'],

    };

    // Modules that only support 'Read'
    const READ_ONLY_MODULES = [
        'DASHBOARD',
        'VENDOR_PAYMENT_OVERDUE_REPORT',
        'PROJECT_EXPENSE_REPORT',
        'PRODUCT_SUMMARY',
        'PROJECT_SUMMARY',
        'VENDOR_SUMMARY',
        'COMPARE_PO',
        'REPORT_LOG',
        'HISTORY_LOG'
    ];

    // Helper to check if a permission type is supported for a module constant
    const isPermissionSupported = (constant, type) => {
        if (type === 'read') return true; // Everyone has Read
        if (READ_ONLY_MODULES.includes(constant)) return false;

        // Modules that don't support Delete or Update but support Create
        if (['VIEW_QUOTATIONS'].includes(constant)) {
            return type === 'create';
        }

        return true;
    };

    // Helper to find module ID by constant
    const getModuleIdByConstant = (constant) => {
        const module = moduleList.find(m => m.constant === constant);
        return module ? module.id : null;
    };

    // Toggle individual permission
    const togglePermission = (moduleId, permissionType) => {
        setPermissions(prev => {
            const currentPermissions = prev[moduleId] || {};
            const newValue = !currentPermissions[permissionType];

            const updatedPermissions = Object.keys(prev).reduce((acc, key) => {
                acc[key] = { ...prev[key] };
                return acc;
            }, {});

            updatedPermissions[moduleId][permissionType] = newValue;

            // Automatically enable 'read' when 'create', 'update', or 'delete' is enabled
            if (newValue && ['create', 'update', 'delete'].includes(permissionType)) {
                updatedPermissions[moduleId].read = true;
            }

            // Dependency logic: if any permission is enabled, ensure 'read' is granted to dependent modules
            if (newValue) {
                const currentModule = moduleList.find(m => m.id === moduleId);
                if (currentModule && DEPENDENCY_MAP[currentModule.constant]) {
                    DEPENDENCY_MAP[currentModule.constant].forEach(depConstant => {
                        const depModuleId = getModuleIdByConstant(depConstant);
                        if (depModuleId && updatedPermissions[depModuleId]) {
                            updatedPermissions[depModuleId].read = true;
                        }
                    });
                }
            }

            return updatedPermissions;
        });
    };

    // Toggle all permissions for a module
    const toggleAllModulePermissions = (moduleId) => {
        const currentModule = moduleList.find(m => m.id === moduleId);
        if (!currentModule) return;

        const supportedTypes = ['create', 'read', 'update', 'delete'].filter(t =>
            isPermissionSupported(currentModule.constant, t)
        );

        const allEnabled = supportedTypes.every(t => permissions[moduleId] && permissions[moduleId][t]);

        setPermissions(prev => {
            const updatedPermissions = { ...prev };
            const modulePerms = { ...(prev[moduleId] || {}) };

            supportedTypes.forEach(t => {
                modulePerms[t] = !allEnabled;
            });

            updatedPermissions[moduleId] = modulePerms;

            // Dependency logic: if enabling all, ensure 'read' is granted to dependent modules
            if (!allEnabled) {
                if (DEPENDENCY_MAP[currentModule.constant]) {
                    DEPENDENCY_MAP[currentModule.constant].forEach(depConstant => {
                        const depModuleId = getModuleIdByConstant(depConstant);
                        if (depModuleId && updatedPermissions[depModuleId]) {
                            updatedPermissions[depModuleId].read = true;
                        }
                    });
                }
            }

            return updatedPermissions;
        });
    };

    // Toggle all permissions for all modules
    const toggleAllGlobalPermissions = () => {
        // Calculate if all current modules are fully enabled (for their supported permissions)
        const allModulesFullyEnabled = moduleList.every(module => {
            const p = permissions[module.id];
            const supportedTypes = ['create', 'read', 'update', 'delete'].filter(t =>
                isPermissionSupported(module.constant, t)
            );
            return p && supportedTypes.every(t => p[t]);
        });

        const newState = !allModulesFullyEnabled;
        const newPermissions = { ...permissions };

        moduleList.forEach(module => {
            const modulePerms = { ...(permissions[module.id] || {}) };
            ['create', 'read', 'update', 'delete'].forEach(t => {
                if (isPermissionSupported(module.constant, t)) {
                    modulePerms[t] = newState;
                } else {
                    modulePerms[t] = false;
                }
            });
            newPermissions[module.id] = modulePerms;
        });

        setPermissions(newPermissions);
    };

    const handleSavePermissions = async () => {
        if (!selectedEmployee) return;

        try {
            setSaving(true);
            const token = localStorage.getItem('accessToken');

            const formattedPermissions = Object.entries(permissions).map(([moduleId, perms]) => ({
                menu_id: parseInt(moduleId),
                can_create: perms.create,
                can_read: perms.read,
                can_update: perms.update,
                can_delete: perms.delete
            })); // Removed .filter(p => p.can_create || p.can_read || p.can_update || p.can_delete)

            const payload = {
                employeeId: selectedEmployee.value,
                permissions: formattedPermissions
            };

            const response = await axios.post(ASSIGN_PERMISSION_API, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                Swal.fire('Success', 'Permissions saved successfully!', 'success');

                // If we updated our own permissions, refresh the global state to update the sidebar
                if (userProfile && selectedEmployee.value === userProfile.id) {
                    refreshUserAccess();
                }
            } else {
                Swal.fire('Error', 'Failed to save permissions: ' + (response.data.message || 'Unknown error'), 'error');
            }
        } catch (error) {
            console.error('Error saving permissions:', error);
            Swal.fire('Error', 'An error occurred while saving permissions.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const activeEmployeesSet = employees.filter(emp =>
        !emp.status || emp.status.toLowerCase() === 'active'
    );

    const employeeOptions = activeEmployeesSet.map(emp => ({
        value: emp.id,
        label: `${emp.firstName} ${emp.lastName} (${emp.userName})`,
    }));


    return (
        <div className="dashboard">
            <div className="permission-header">
                <h2 className="section-title" style={{ margin: 0 }}>Permission Rights Management</h2>
            </div>

            <div className="card-container-permissions">
                {/* Employee Selection */}
                <div className="employee-selection-card">
                    <div className="selection-header">
                        <MdPerson size={24} />
                        <h3>Select Associate</h3>
                    </div>
                    <PaginatedSelect
                        apiUrl={GET_ALL_EMPLOYEES_API}
                        mapResponse={(emp) => ({
                            value: emp.id || emp._id,
                            label: `${emp.firstName} ${emp.lastName} (${emp.userName})`,
                        })}
                        onChange={handleEmployeeChange}
                        value={selectedEmployee}
                        placeholder="-- Select an Associate --"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isClearable
                    />


                </div>

                {/* Permissions Table */}
                {fetchingPermissions ? (
                    <div className="permissions-card">
                        <Loader text="Fetching Associate permissions..." />
                    </div>
                ) : selectedEmployee && (
                    <div className="permissions-card">
                        <div className="permissions-header">
                            <div className="permissions-header-content">
                                <h3>Module Permissions</h3>
                                <p className="permissions-subtitle">
                                    Configure CRUD permissions for {selectedEmployee?.label}
                                </p>
                            </div>
                            <div className="global-toggle-container">
                                <span className="global-toggle-label">Toggle All Modules</span>
                                <label className="switch global-switch">
                                    <input
                                        type="checkbox"
                                        checked={moduleList.length > 0 && moduleList.every(m => {
                                            const supported = ['create', 'read', 'update', 'delete'].filter(t =>
                                                isPermissionSupported(m.constant, t)
                                            );
                                            return supported.every(t => permissions[m.id]?.[t]);
                                        })}
                                        onChange={toggleAllGlobalPermissions}
                                    />
                                    <span className="slider"></span>
                                </label>
                            </div>
                        </div>

                        {moduleList.length === 0 ? (
                            <NoData title="No Modules" message="No modules available for configuration." />
                        ) : (
                            <>
                                <div className="permissions-table-container">
                                    <table className="permissions-table">
                                        <thead>
                                            <tr>
                                                <th>Module</th>
                                                <th>Create</th>
                                                <th>Read</th>
                                                <th>Update</th>
                                                <th>Delete</th>
                                                <th>All</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {moduleList.map(module => (
                                                <tr key={module.id}>
                                                    <td className="module-name">{module.name}</td>
                                                    <td>
                                                        {isPermissionSupported(module.constant, 'create') ? (
                                                            <label className="switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permissions[module.id]?.create || false}
                                                                    onChange={() => togglePermission(module.id, 'create')}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        ) : <span className="no-perm-dash">-</span>}
                                                    </td>
                                                    <td>
                                                        {isPermissionSupported(module.constant, 'read') ? (
                                                            <label className="switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permissions[module.id]?.read || false}
                                                                    onChange={() => togglePermission(module.id, 'read')}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        ) : <span className="no-perm-dash">-</span>}
                                                    </td>
                                                    <td>
                                                        {isPermissionSupported(module.constant, 'update') ? (
                                                            <label className="switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permissions[module.id]?.update || false}
                                                                    onChange={() => togglePermission(module.id, 'update')}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        ) : <span className="no-perm-dash">-</span>}
                                                    </td>
                                                    <td>
                                                        {isPermissionSupported(module.constant, 'delete') ? (
                                                            <label className="switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={permissions[module.id]?.delete || false}
                                                                    onChange={() => togglePermission(module.id, 'delete')}
                                                                />
                                                                <span className="slider"></span>
                                                            </label>
                                                        ) : <span className="no-perm-dash">-</span>}
                                                    </td>
                                                    <td>
                                                        <label className="switch switch-all">
                                                            <input
                                                                type="checkbox"
                                                                checked={['create', 'read', 'update', 'delete']
                                                                    .filter(t => isPermissionSupported(module.constant, t))
                                                                    .every(t => permissions[module.id]?.[t])
                                                                }
                                                                onChange={() => toggleAllModulePermissions(module.id)}
                                                            />
                                                            <span className="slider"></span>
                                                        </label>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="permissions-mobile-view">
                                    {moduleList.map(module => (
                                        <div key={module.id} className="permission-module-card">
                                            <div className="module-card-header">
                                                <h4>{module.name}</h4>
                                                <div className="all-permission-toggle">
                                                    <span>All</span>
                                                    <label className="switch switch-all">
                                                        <input
                                                            type="checkbox"
                                                            checked={['create', 'read', 'update', 'delete']
                                                                .filter(t => isPermissionSupported(module.constant, t))
                                                                .every(t => permissions[module.id]?.[t])
                                                            }
                                                            onChange={() => toggleAllModulePermissions(module.id)}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="module-card-body">
                                                {isPermissionSupported(module.constant, 'create') && (
                                                    <div className="permission-row">
                                                        <span>Create</span>
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={permissions[module.id]?.create || false}
                                                                onChange={() => togglePermission(module.id, 'create')}
                                                            />
                                                            <span className="slider"></span>
                                                        </label>
                                                    </div>
                                                )}
                                                {isPermissionSupported(module.constant, 'read') && (
                                                    <div className="permission-row">
                                                        <span>Read</span>
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={permissions[module.id]?.read || false}
                                                                onChange={() => togglePermission(module.id, 'read')}
                                                            />
                                                            <span className="slider"></span>
                                                        </label>
                                                    </div>
                                                )}
                                                {isPermissionSupported(module.constant, 'update') && (
                                                    <div className="permission-row">
                                                        <span>Update</span>
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={permissions[module.id]?.update || false}
                                                                onChange={() => togglePermission(module.id, 'update')}
                                                            />
                                                            <span className="slider"></span>
                                                        </label>
                                                    </div>
                                                )}
                                                {isPermissionSupported(module.constant, 'delete') && (
                                                    <div className="permission-row">
                                                        <span>Delete</span>
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={permissions[module.id]?.delete || false}
                                                                onChange={() => togglePermission(module.id, 'delete')}
                                                            />
                                                            <span className="slider"></span>
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="permissions-actions">
                                    {(hasPermission('PERMISSIONS', 'create') || hasPermission('PERMISSIONS', 'update')) && (
                                        <button
                                            className="save-permissions-btn"
                                            onClick={handleSavePermissions}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving...' : 'Save Permissions'}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {!selectedEmployee && (
                    <div className="no-selection-message">
                        <MdPerson size={64} />
                        <p>Please select an associate to manage permissions</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PermissionRights;
